import React, { useEffect, useState, useRef, lazy } from 'react';
interface FeatureSectionV3Props {
  'data-id'?: string;
  imageSrc?: string;
}
export const FeatureSectionV3: React.FC<FeatureSectionV3Props> = ({
  'data-id': dataId,
  imageSrc = 'https://uploadthingy.s3.us-west-1.amazonaws.com/kf79sfKsVSicwMJU4jfmzN/Screenshot_2025-11-12_at_8.52.23_AM.png'
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  return <section id="features" ref={sectionRef} className="relative w-full bg-gradient-to-b from-[#121212] via-[#0f0f0f] to-[#121212] py-20 md:py-24 lg:py-32 px-6 md:px-8 overflow-hidden" aria-labelledby="bulk-offers-heading" data-id="element-1089">
      {/* Subtle background decoration */}
      <div className="absolute inset-0 opacity-30" data-id="element-1090">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#3ab795] rounded-full blur-[120px] opacity-10" data-id="element-1091" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#3ab795] rounded-full blur-[120px] opacity-10" data-id="element-1092" />
      </div>

      <div className="relative max-w-[1280px] mx-auto flex flex-col gap-16 md:gap-20 lg:gap-24" data-id="element-1093">
        {/* Header Section */}
        <div className="max-w-4xl mx-auto flex flex-col items-center gap-6 text-center" data-id="element-1094">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#3ab795]/10 border border-[#3ab795]/20 backdrop-blur-sm" data-id="element-1095">
            <div className="w-2 h-2 rounded-full bg-[#3ab795] animate-pulse" data-id="element-1096" />
            <p className="text-sm md:text-base font-medium text-[#3ab795] tracking-wide uppercase" data-id="element-1097">
              Key Features
            </p>
          </div>
          <div className="flex flex-col items-center gap-6" data-id="element-1098">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight" data-id="element-1099">
              Key Features That Make{' '}
              <span className="text-[#3ab795] relative inline-block" data-id="element-1100">
                AcquireFlow
                <span className="absolute -bottom-2 left-0 right-0 h-1 bg-[#3ab795]/30 rounded-full" data-id="element-1101"></span>
              </span>{' '}
              Unstoppable
            </h2>
            <p className="text-lg md:text-xl lg:text-2xl text-[#b8bcc8] max-w-3xl leading-relaxed font-light" data-id="element-1102">
              Your AI-powered partner for scaling MLS deal flow — built to find,
              analyze, and send offers faster than any human team could.
            </p>
          </div>
        </div>
        {/* First Section: Bulk Offers */}
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20" data-id="element-1103">
          {/* Left: Image Card - MOBILE OPTIMIZED */}
          <div className="w-full lg:w-3/5 relative" data-id="element-1104">
            {/* White glow effect behind card */}
            <div className="absolute inset-0 rounded-2xl blur-3xl bg-white opacity-30 -z-10 animate-pulse" style={{
            transform: 'scale(0.92)',
            animationDuration: '3s'
          }} data-id="element-1105" />

            <div className="relative w-full rounded-xl sm:rounded-2xl border border-zinc-200 sm:border-2 shadow-lg sm:shadow-xl/15 overflow-hidden transition-all duration-300 sm:duration-500 hover:scale-[1.01] sm:hover:scale-[1.02] hover:shadow-xl sm:hover:shadow-2xl" data-id="element-1106">
              {/* Mobile uses min-height instead of fixed aspect ratio */}
              <div className="w-full relative bg-white min-h-[600px] sm:min-h-0 sm:aspect-[4/3] lg:aspect-[16/12]" data-id="element-1107">
                {/* Main image - Replaced with interactive UI mockup */}
                <div className="absolute inset-0 flex flex-col bg-white" data-id="element-1108">
                  {/* Header - Responsive */}
                  <div className="bg-white px-4 sm:px-6 py-3 sm:py-4 border-b border-zinc-200" data-id="element-1109">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0" data-id="element-1110">
                      <div data-id="element-1111">
                        <h3 className="text-base sm:text-lg md:text-xl font-bold text-[#1D1D1D]" data-id="element-1112">
                          Deal Finder
                        </h3>
                        <p className="text-xs sm:text-sm text-zinc-500" data-id="element-1113">
                          Find and select properties
                        </p>
                      </div>
                      {/* Icons - Hide on mobile */}
                      <div className="hidden sm:flex gap-2" data-id="element-1114">
                        <div className="w-8 h-8 rounded-full bg-[#3ab795]/10 flex items-center justify-center text-[#3ab795]" data-id="element-1115">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1116">
                            <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1117" />
                          </svg>
                        </div>
                        <div className="w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-600" data-id="element-1118">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1119">
                            <path d="M15 17H20L18.5951 15.5951C18.2141 15.2141 18 14.6973 18 14.1585V11C18 8.38757 16.3304 6.16509 14 5.34142V5C14 3.89543 13.1046 3 12 3C10.8954 3 10 3.89543 10 5V5.34142C7.66962 6.16509 6 8.38757 6 11V14.1585C6 14.6973 5.78595 15.2141 5.40493 15.5951L4 17H9M15 17V18C15 19.6569 13.6569 21 12 21C10.3431 21 9 19.6569 9 18V17M15 17H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1120" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Search and Filter Bar - Mobile Optimized */}
                  <div className="bg-[#fafafa] px-3 sm:px-6 py-2 sm:py-3 border-b border-zinc-200" data-id="element-1121">
                    <div className="flex flex-col gap-2 sm:gap-3" data-id="element-1122">
                      {/* Search - Full width on mobile */}
                      <div className="relative w-full" data-id="element-1123">
                        <input type="text" placeholder="Search..." className="w-full bg-white border border-zinc-200 rounded-lg py-2 pl-9 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#3ab795]/30" data-id="element-1124" />
                        <svg className="absolute left-3 top-2.5 w-4 h-4 text-zinc-500" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1125">
                          <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1126" />
                        </svg>
                      </div>

                      {/* Buttons and badge - Horizontal scroll on mobile */}
                      <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-3 px-3 sm:mx-0 sm:px-0 sm:pb-0" data-id="element-1127">
                        <button className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 bg-white border border-zinc-200 rounded-lg text-sm font-medium text-zinc-700 sm:hover:bg-zinc-50 transition-colors" data-id="element-1128">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1129">
                            <path d="M3 6H21M3 12H21M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1130" />
                          </svg>
                          <span className="hidden sm:inline" data-id="element-1131">Filters</span>
                        </button>
                        {/* Hide Sort and More on mobile */}
                        <button className="hidden sm:flex items-center gap-1.5 px-3 py-2 bg-white border border-zinc-200 rounded-lg text-sm font-medium text-zinc-700 hover:bg-zinc-50 transition-colors" data-id="element-1132">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1133">
                            <path d="M3 4H16M3 12H12M3 20H20M21 4V8M21 8H17M21 8L17 4M21 12V16M21 16H17M21 16L17 12M9 20V16M9 16H5M9 16L5 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1134" />
                          </svg>
                          Sort
                        </button>
                        <button className="hidden sm:flex items-center gap-1.5 px-3 py-2 bg-white border border-zinc-200 rounded-lg text-sm font-medium text-zinc-700 hover:bg-zinc-50 transition-colors" data-id="element-1135">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1136">
                            <path d="M9 2H15M12 10V15M12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1137" />
                          </svg>
                          More
                        </button>
                        <div className="flex-shrink-0 bg-[#3ab795]/10 border border-[#3ab795]/20 text-[#3ab795] font-medium rounded-lg px-3 py-2 text-sm flex items-center gap-1.5" data-id="element-1138">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1139">
                            <path d="M9 11L12 14L22 4M21 12V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1140" />
                          </svg>
                          250
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Property Grid - Single column on mobile */}
                  <div className="flex-1 overflow-y-auto p-3 sm:p-4 bg-[#fafafa] grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4" data-id="element-1141">
                    {/* Property Card 1 - Mobile optimized */}
                    <div className="bg-white rounded-lg border-2 border-[#3ab795] p-2.5 sm:p-3 flex flex-col sm:flex-row gap-3 shadow-sm relative" data-id="element-1142">
                      <div className="absolute top-2 sm:top-3 left-2 sm:left-3 w-5 h-5 bg-[#3ab795] rounded flex items-center justify-center z-10" data-id="element-1143">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1144">
                          <path d="M5 12L10 17L20 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1145" />
                        </svg>
                      </div>
                      <div className="w-full sm:w-20 md:w-24 h-32 sm:h-20 md:h-24 bg-zinc-100 rounded overflow-hidden flex-shrink-0 mt-7 sm:mt-6" data-id="element-1146">
                        <img src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=200&h=200&auto=format&fit=crop" alt="Modern single-family home at 3737 Casa Del Sol Ln" className="w-full h-full object-cover" loading="lazy" data-id="element-1147" />
                      </div>
                      <div className="flex-1 min-w-0 space-y-1.5 sm:space-y-2" data-id="element-1148">
                        <h4 className="font-semibold text-sm sm:text-base text-[#1D1D1D] truncate" data-id="element-1149">
                          3737 Casa Del Sol Ln
                        </h4>
                        <p className="text-xs sm:text-sm text-zinc-600" data-id="element-1150">
                          Dallas, TX 75228
                        </p>
                        <div className="flex flex-wrap gap-2 sm:gap-3 text-xs sm:text-sm" data-id="element-1151">
                          <div data-id="element-1152">
                            <span className="text-zinc-500" data-id="element-1153">Beds: </span>
                            <span className="font-medium" data-id="element-1154">3</span>
                          </div>
                          <div data-id="element-1155">
                            <span className="text-zinc-500" data-id="element-1156">Baths: </span>
                            <span className="font-medium" data-id="element-1157">2</span>
                          </div>
                          <div data-id="element-1158">
                            <span className="text-zinc-500" data-id="element-1159">SqFt: </span>
                            <span className="font-medium" data-id="element-1160">1,845</span>
                          </div>
                        </div>
                        <div className="flex flex-col xs:flex-row justify-between items-start xs:items-center gap-2 pt-1" data-id="element-1161">
                          <span className="text-base sm:text-lg font-bold text-[#3ab795]" data-id="element-1162">
                            $343,900
                          </span>
                          <button className="text-xs bg-[#3ab795]/10 text-[#3ab795] py-1 px-2 rounded font-medium sm:hover:bg-[#3ab795]/20 transition-colors" data-id="element-1163">
                            View
                          </button>
                        </div>
                      </div>
                    </div>
                    {/* Property Card 2 - Mobile optimized */}
                    <div className="bg-white rounded-lg border-2 border-[#3ab795] p-2.5 sm:p-3 flex flex-col sm:flex-row gap-3 shadow-sm relative" data-id="element-1164">
                      <div className="absolute top-2 sm:top-3 left-2 sm:left-3 w-5 h-5 bg-[#3ab795] rounded flex items-center justify-center z-10" data-id="element-1165">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1166">
                          <path d="M5 12L10 17L20 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1167" />
                        </svg>
                      </div>
                      <div className="w-full sm:w-20 md:w-24 h-32 sm:h-20 md:h-24 bg-zinc-100 rounded overflow-hidden flex-shrink-0 mt-7 sm:mt-6" data-id="element-1168">
                        <img src="https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?q=80&w=200&h=200&auto=format&fit=crop" alt="Contemporary house at 8324 Coolgreene Dr" className="w-full h-full object-cover" loading="lazy" data-id="element-1169" />
                      </div>
                      <div className="flex-1 min-w-0 space-y-1.5 sm:space-y-2" data-id="element-1170">
                        <h4 className="font-semibold text-sm sm:text-base text-[#1D1D1D] truncate" data-id="element-1171">
                          8324 Coolgreene Dr
                        </h4>
                        <p className="text-xs sm:text-sm text-zinc-600" data-id="element-1172">
                          Dallas, TX 75228
                        </p>
                        <div className="flex flex-wrap gap-2 sm:gap-3 text-xs sm:text-sm" data-id="element-1173">
                          <div data-id="element-1174">
                            <span className="text-zinc-500" data-id="element-1175">Beds: </span>
                            <span className="font-medium" data-id="element-1176">4</span>
                          </div>
                          <div data-id="element-1177">
                            <span className="text-zinc-500" data-id="element-1178">Baths: </span>
                            <span className="font-medium" data-id="element-1179">2.5</span>
                          </div>
                          <div data-id="element-1180">
                            <span className="text-zinc-500" data-id="element-1181">SqFt: </span>
                            <span className="font-medium" data-id="element-1182">2,100</span>
                          </div>
                        </div>
                        <div className="flex flex-col xs:flex-row justify-between items-start xs:items-center gap-2 pt-1" data-id="element-1183">
                          <span className="text-base sm:text-lg font-bold text-[#3ab795]" data-id="element-1184">
                            $365,000
                          </span>
                          <button className="text-xs bg-[#3ab795]/10 text-[#3ab795] py-1 px-2 rounded font-medium sm:hover:bg-[#3ab795]/20 transition-colors" data-id="element-1185">
                            View
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Bottom action bar - Mobile optimized */}
                  <div className="bg-white/95 backdrop-blur-sm py-3 sm:py-4 px-4 sm:px-5 border-t border-zinc-200" data-id="element-1186">
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-0 sm:justify-between sm:items-center" data-id="element-1187">
                      <span className="text-xs sm:text-sm font-medium text-[#1D1D1D] text-center sm:text-left" data-id="element-1188">
                        <span className="sm:hidden" data-id="element-1189">
                          Ready to send 250 offers
                        </span>
                        <span className="hidden sm:inline" data-id="element-1190">
                          Ready to send offers to all selected properties
                        </span>
                      </span>
                      <button aria-label="Send Bulk Offer" className="w-full sm:w-auto bg-[#3ab795] sm:hover:bg-[#2da07d] text-white font-medium py-3 sm:py-2.5 px-5 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 min-h-[44px]" onClick={e => e.preventDefault()} data-id="element-1191">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1192">
                          <path d="M22 2L11 13M22 2L15 22L11 13M11 13L2 9L22 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1193" />
                        </svg>
                        <span className="text-sm sm:text-base" data-id="element-1194">
                          Send Bulk Offer
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Caption explaining the image */}
            <p className="text-sm text-zinc-500 mt-3 text-center" data-id="element-1195">
              AcquireFlow's Deal Finder with bulk selection and one-click offer
              sending
            </p>
          </div>
          {/* Right: Content - MORE FOCUSED */}
          <div className="w-full lg:w-2/5 flex flex-col gap-8" data-id="element-1196">
            <div className="flex flex-col gap-4" data-id="element-1197">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#3ab795]/10 border border-[#3ab795]/20 backdrop-blur-sm w-fit" data-id="element-1198">
                <div className="w-2 h-2 rounded-full bg-[#3ab795] animate-pulse" data-id="element-1199" />
                <p className="text-sm md:text-base font-medium text-[#3ab795] tracking-wide uppercase" data-id="element-1200">
                  Bulk Offer System
                </p>
              </div>
              <h2 id="bulk-offers-heading" className="text-3xl md:text-4xl font-bold text-white leading-tight" data-id="element-1201">
                Send <span className="text-[#3ab795]" data-id="element-1202">Hundreds of Offers</span>{' '}
                With One Click
              </h2>
              <p className="text-lg text-[#b8bcc8] leading-relaxed" data-id="element-1203">
                Stop wasting time sending offers one by one. AcquireFlow lets
                you select multiple properties from your target markets and
                instantly send personalized offers at scale.
              </p>
            </div>
            <div className="flex flex-col gap-5 mt-2" data-id="element-1204">
              {[{
              title: 'Bulk Property Selection',
              description: 'Filter and select properties in bulk based on your criteria — location, price, bedrooms, time on market, and more.',
              delay: 0
            }, {
              title: 'Automated Pricing Rules',
              description: 'Set custom formulas (like 65% of list price) and AcquireFlow automatically calculates the right offer amount for each property.',
              delay: 100
            }, {
              title: 'Personalized Mass Delivery',
              description: 'Each offer is personalized with the property details and agent information, then delivered instantly via email or MLS messaging.',
              delay: 200
            }].map((feature, index) => <div key={index} className="flex items-start gap-3 p-3 rounded-xl hover:bg-white/5 transition-all duration-300 opacity-0 translate-y-4" style={{
              animation: isVisible ? `fadeInUp 0.6s ease-out ${feature.delay}ms forwards` : 'none'
            }} data-id="element-1205">
                  <div className="w-7 h-7 rounded-full bg-[#3ab795] flex items-center justify-center flex-shrink-0 mt-1" data-id="element-1206">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-white" data-id="element-1207">
                      <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-1208" />
                    </svg>
                  </div>
                  <div data-id="element-1209">
                    <h3 className="text-lg font-semibold text-white" data-id="element-1210">
                      {feature.title}
                    </h3>
                    <p className="text-[#b8bcc8]" data-id="element-1211">{feature.description}</p>
                  </div>
                </div>)}
            </div>
            <div className="mt-6" data-id="element-1212">
              <a href="#pricing" className="inline-flex items-center gap-3 bg-white hover:bg-gray-100 text-[#1D1D1D] rounded-xl pl-7 pr-5 py-3.5 transition-all duration-300 font-medium text-lg hover:shadow-lg hover:scale-[1.02]" aria-label="Start sending bulk offers" data-id="element-1213">
                Start Sending Bulk Offers
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1214">
                  <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1215" />
                </svg>
              </a>
              <p className="text-sm text-[#b8bcc8] mt-3" data-id="element-1216">
                Scale your outreach 10x without adding to your team
              </p>
            </div>
          </div>
        </div>
        {/* Second Section: LOI Generation */}
        <div className="flex flex-col lg:flex-row-reverse items-center gap-12 lg:gap-20" data-id="element-1217">
          {/* Right: Image Card - LOI Templates - MOBILE OPTIMIZED */}
          <div className="w-full lg:w-3/5" data-id="element-1218">
            <div className="relative w-full rounded-xl sm:rounded-2xl border border-zinc-200 sm:border-2 shadow-lg sm:shadow-xl/15 overflow-hidden transition-all duration-300 sm:duration-500" data-id="element-1219">
              {/* Mobile uses min-height, tablet+ uses aspect ratio */}
              <div className="w-full relative bg-white min-h-[500px] sm:min-h-0 sm:aspect-[16/12]" data-id="element-1220">
                {/* LOI Generation UI */}
                <div className="absolute inset-0 flex flex-col" data-id="element-1221">
                  {/* Header - Mobile Optimized */}
                  <div className="bg-white px-3 sm:px-4 md:px-6 py-3 sm:py-4 border-b border-zinc-200 flex-shrink-0" data-id="element-1222">
                    <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-bold text-[#1D1D1D]" data-id="element-1223">
                      LOI Generator
                    </h3>
                    <p className="text-xs sm:text-sm text-zinc-500" data-id="element-1224">
                      Configure your offer settings for 250 selected properties
                    </p>
                  </div>

                  {/* Main Content Area - Scrollable */}
                  <div className="flex-1 overflow-y-auto" data-id="element-1225">
                    <div className="flex flex-col lg:flex-row min-h-full" data-id="element-1226">
                      {/* Left Panel - Settings - Mobile Optimized */}
                      <div className="w-full lg:w-1/2 p-3 sm:p-4 lg:p-5 border-b lg:border-b-0 lg:border-r border-zinc-200" data-id="element-1227">
                        <div className="mb-4 sm:mb-5" data-id="element-1228">
                          <h4 className="text-sm sm:text-base font-medium text-[#1D1D1D] mb-2 sm:mb-3" data-id="element-1229">
                            LOI Template
                          </h4>
                          <div className="space-y-2 sm:space-y-2.5" data-id="element-1230">
                            {/* Standard Cash Offer - Mobile Optimized */}
                            <div className="border-2 border-[#3ab795] rounded-lg p-2.5 sm:p-3 bg-[#f8fdfb]" data-id="element-1231">
                              <div className="flex items-center gap-2 sm:gap-2.5" data-id="element-1232">
                                <div className="w-7 h-7 sm:w-8 sm:h-8 bg-[#3ab795]/10 rounded-full flex items-center justify-center text-[#3ab795] flex-shrink-0" data-id="element-1233">
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1234">
                                    <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z" fill="currentColor" data-id="element-1235" />
                                    <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" fill="currentColor" data-id="element-1236" />
                                  </svg>
                                </div>
                                <div className="min-w-0" data-id="element-1237">
                                  <h5 className="font-semibold text-sm sm:text-base text-[#1D1D1D]" data-id="element-1238">
                                    Standard Cash Offer
                                  </h5>
                                  <p className="text-xs sm:text-sm text-zinc-500 hidden sm:block" data-id="element-1239">
                                    Quick closing with cash offer
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Subject To Acquisition - Mobile Optimized */}
                            <div className="border border-zinc-200 rounded-lg p-2.5 sm:p-3" data-id="element-1240">
                              <div className="flex items-center gap-2 sm:gap-2.5" data-id="element-1241">
                                <div className="w-7 h-7 sm:w-8 sm:h-8 bg-zinc-100 rounded-full flex items-center justify-center text-zinc-600 flex-shrink-0" data-id="element-1242">
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1243">
                                    <path d="M3 12L5 10M5 10L12 3L19 10M5 10V20C5 20.5523 5.44772 21 6 21H9M19 10L21 12M19 10V20C19 20.5523 18.5523 21 18 21H15M9 21C9.55228 21 10 20.5523 10 20V16C10 15.4477 10.4477 15 11 15H13C13.5523 15 14 15.4477 14 16V20C14 20.5523 14.4477 21 15 21M9 21H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1244" />
                                  </svg>
                                </div>
                                <div className="min-w-0" data-id="element-1245">
                                  <h5 className="font-semibold text-sm sm:text-base text-[#1D1D1D]" data-id="element-1246">
                                    Subject To Acquisition
                                  </h5>
                                  <p className="text-xs sm:text-sm text-zinc-500 hidden sm:block" data-id="element-1247">
                                    Take over existing financing
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Offer Settings - Mobile Optimized */}
                        <div className="mb-4 sm:mb-5" data-id="element-1248">
                          <h4 className="text-sm sm:text-base font-medium text-[#1D1D1D] mb-2 sm:mb-3" data-id="element-1249">
                            Offer Settings
                          </h4>

                          {/* Offer Percentage Slider */}
                          <div className="mb-4 sm:mb-5" data-id="element-1250">
                            <div className="flex justify-between items-center mb-2" data-id="element-1251">
                              <span className="text-xs sm:text-sm text-zinc-600" data-id="element-1252">
                                Offer Percentage
                              </span>
                              <span className="text-sm sm:text-base font-semibold text-[#1D1D1D]" data-id="element-1253">
                                70%
                              </span>
                            </div>
                            <div className="relative h-2 bg-zinc-100 rounded-full mb-2" data-id="element-1254">
                              <div className="absolute left-0 top-0 h-2 w-[70%] bg-[#3ab795] rounded-full" data-id="element-1255"></div>
                              <div className="absolute h-4 w-4 bg-white border-2 border-[#3ab795] rounded-full top-1/2 left-[70%] -translate-y-1/2 -translate-x-1/2 shadow-sm" data-id="element-1256"></div>
                            </div>
                            <div className="flex justify-between text-xs text-zinc-400" data-id="element-1257">
                              <span data-id="element-1258">50%</span>
                              <span data-id="element-1259">75%</span>
                              <span data-id="element-1260">100%</span>
                            </div>
                          </div>

                          {/* Delivery Methods - Mobile Optimized */}
                          <div className="mb-4 sm:mb-5" data-id="element-1261">
                            <h5 className="text-xs sm:text-sm font-medium text-zinc-600 mb-2" data-id="element-1262">
                              Delivery Methods
                            </h5>
                            <div className="flex gap-2" data-id="element-1263">
                              <div className="border-2 border-[#3ab795] bg-[#f8fdfb] rounded-lg py-2 px-3 flex items-center justify-center gap-2 flex-1 min-h-[44px]" data-id="element-1264">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#3ab795] flex-shrink-0" data-id="element-1265">
                                  <path d="M3 8L10.7208 13.1631C11.4638 13.6735 12.4854 13.6735 13.2284 13.1631L21 8M5 19H19C20.1046 19 21 18.1046 21 17V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V17C3 18.1046 3.89543 19 5 19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1266" />
                                </svg>
                                <span className="text-sm sm:text-base font-medium text-[#1D1D1D]" data-id="element-1267">
                                  Email
                                </span>
                              </div>
                              <div className="border border-zinc-200 rounded-lg py-2 px-3 flex items-center justify-center gap-2 flex-1 min-h-[44px]" data-id="element-1268">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-zinc-600 flex-shrink-0" data-id="element-1269">
                                  <path d="M8 9H16M8 13H14M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1270" />
                                </svg>
                                <span className="text-sm sm:text-base font-medium text-zinc-600" data-id="element-1271">
                                  Text
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Scheduling - Mobile Optimized */}
                          <div data-id="element-1272">
                            <h5 className="text-xs sm:text-sm font-medium text-zinc-600 mb-2" data-id="element-1273">
                              Scheduling
                            </h5>
                            <div className="border-2 border-[#3ab795] bg-[#f8fdfb] rounded-lg p-2.5 sm:p-3 flex items-start gap-2 sm:gap-2.5" data-id="element-1274">
                              <div className="text-[#3ab795] mt-0.5 flex-shrink-0" data-id="element-1275">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1276">
                                  <path d="M5 9L11 15L21 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1277" />
                                </svg>
                              </div>
                              <div className="min-w-0" data-id="element-1278">
                                <h5 className="font-medium text-sm sm:text-base text-[#1D1D1D]" data-id="element-1279">
                                  Send Immediately
                                </h5>
                                <p className="text-xs sm:text-sm text-zinc-500 hidden sm:block" data-id="element-1280">
                                  Offers sent as soon as you confirm
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Right Panel - Properties - Mobile Optimized */}
                      <div className="w-full lg:w-1/2 p-3 sm:p-4 lg:p-5 bg-[#fafafa]" data-id="element-1281">
                        <h4 className="text-sm sm:text-base font-medium text-[#1D1D1D] mb-2 sm:mb-3" data-id="element-1282">
                          Selected Properties (250)
                        </h4>

                        <div className="space-y-2 sm:space-y-2.5 mb-3 sm:mb-4" data-id="element-1283">
                          {/* Property 1 - Mobile Optimized */}
                          <div className="bg-white rounded-lg border border-zinc-200 p-2.5 sm:p-3" data-id="element-1284">
                            <div className="flex gap-2.5 sm:gap-3" data-id="element-1285">
                              <div className="w-16 h-16 sm:w-14 sm:h-14 bg-zinc-100 rounded overflow-hidden flex-shrink-0" data-id="element-1286">
                                <img src="https://images.unsplash.com/photo-1568605114967-8130f3a36994?q=80&w=200&h=200&auto=format&fit=crop" alt="Property listing for 3737 Casa Del Sol Ln" className="w-full h-full object-cover" loading="lazy" data-id="element-1287" />
                              </div>
                              <div className="flex-1 min-w-0 space-y-1" data-id="element-1288">
                                <h5 className="font-medium text-sm sm:text-base text-[#1D1D1D] truncate" data-id="element-1289">
                                  3737 Casa Del Sol Ln
                                </h5>
                                <p className="text-xs sm:text-sm text-zinc-500" data-id="element-1290">
                                  Dallas, TX
                                </p>
                                <div className="flex flex-col xs:flex-row xs:justify-between gap-1 pt-0.5" data-id="element-1291">
                                  <div className="flex items-baseline gap-1" data-id="element-1292">
                                    <span className="text-xs text-zinc-500" data-id="element-1293">
                                      List:
                                    </span>
                                    <span className="text-sm sm:text-base font-semibold text-[#3ab795]" data-id="element-1294">
                                      $343,900
                                    </span>
                                  </div>
                                  <div className="flex items-baseline gap-1" data-id="element-1295">
                                    <span className="text-xs text-zinc-500" data-id="element-1296">
                                      Offer:
                                    </span>
                                    <span className="text-sm sm:text-base font-medium text-zinc-600" data-id="element-1297">
                                      $240,730
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Property 2 - Mobile Optimized */}
                          <div className="bg-white rounded-lg border border-zinc-200 p-2.5 sm:p-3" data-id="element-1298">
                            <div className="flex gap-2.5 sm:gap-3" data-id="element-1299">
                              <div className="w-16 h-16 sm:w-14 sm:h-14 bg-zinc-100 rounded overflow-hidden flex-shrink-0" data-id="element-1300">
                                <img src="https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?q=80&w=200&h=200&auto=format&fit=crop" alt="Property listing for 8324 Coolgreene Dr" className="w-full h-full object-cover" loading="lazy" data-id="element-1301" />
                              </div>
                              <div className="flex-1 min-w-0 space-y-1" data-id="element-1302">
                                <h5 className="font-medium text-sm sm:text-base text-[#1D1D1D] truncate" data-id="element-1303">
                                  8324 Coolgreene Dr
                                </h5>
                                <p className="text-xs sm:text-sm text-zinc-500" data-id="element-1304">
                                  Dallas, TX
                                </p>
                                <div className="flex flex-col xs:flex-row xs:justify-between gap-1 pt-0.5" data-id="element-1305">
                                  <div className="flex items-baseline gap-1" data-id="element-1306">
                                    <span className="text-xs text-zinc-500" data-id="element-1307">
                                      List:
                                    </span>
                                    <span className="text-sm sm:text-base font-semibold text-[#3ab795]" data-id="element-1308">
                                      $365,000
                                    </span>
                                  </div>
                                  <div className="flex items-baseline gap-1" data-id="element-1309">
                                    <span className="text-xs text-zinc-500" data-id="element-1310">
                                      Offer:
                                    </span>
                                    <span className="text-sm sm:text-base font-medium text-zinc-600" data-id="element-1311">
                                      $255,500
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Totals - Mobile Optimized */}
                        <div className="border-t border-zinc-200 pt-2.5 sm:pt-3 mb-3 sm:mb-4" data-id="element-1312">
                          <div className="flex justify-between mb-1.5" data-id="element-1313">
                            <span className="text-xs sm:text-sm text-zinc-600" data-id="element-1314">
                              Total Value:
                            </span>
                            <span className="text-sm sm:text-base font-semibold text-[#1D1D1D]" data-id="element-1315">
                              $70,325,254
                            </span>
                          </div>
                          <div className="flex justify-between" data-id="element-1316">
                            <span className="text-xs sm:text-sm text-zinc-600" data-id="element-1317">
                              Total Offer Amount:
                            </span>
                            <span className="text-sm sm:text-base font-semibold text-[#3ab795]" data-id="element-1318">
                              $49,227,678
                            </span>
                          </div>
                        </div>

                        {/* Preview Button - Mobile Optimized Touch Target */}
                        <button className="w-full flex items-center justify-center gap-2 text-[#3ab795] font-medium py-2.5 sm:py-3 border border-[#3ab795]/30 bg-[#3ab795]/5 rounded-lg sm:hover:bg-[#3ab795]/10 transition-colors min-h-[44px]" data-id="element-1319">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#3ab795] flex-shrink-0" data-id="element-1320">
                            <path d="M2.42012 12.7132C2.28394 12.4975 2.21584 12.3897 2.17772 12.2234C2.14909 12.0985 2.14909 11.9015 2.17772 11.7766C2.21584 11.6103 2.28394 11.5025 2.42012 11.2868C3.54553 9.50484 6.8954 5 12.0004 5C17.1054 5 20.4553 9.50484 21.5807 11.2868C21.7169 11.5025 21.785 11.6103 21.8231 11.7766C21.8517 11.9015 21.8517 12.0985 21.8231 12.2234C21.785 12.3897 21.7169 12.4975 21.5807 12.7132C20.4553 14.4952 17.1054 19 12.0004 19C6.8954 19 3.54553 14.4952 2.42012 12.7132Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1321" />
                            <path d="M12.0004 15C13.6573 15 15.0004 13.6569 15.0004 12C15.0004 10.3431 13.6573 9 12.0004 9C10.3435 9 9.0004 10.3431 9.0004 12C9.0004 13.6569 10.3435 15 12.0004 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1322" />
                          </svg>
                          <span className="text-sm sm:text-base" data-id="element-1323">
                            Preview Delivery
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Footer Actions - Mobile Optimized with Proper Touch Targets */}
                  <div className="bg-white px-3 sm:px-4 md:px-6 py-3 sm:py-4 border-t border-zinc-200 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 flex-shrink-0" data-id="element-1324">
                    <button className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg border border-zinc-200 text-zinc-700 font-medium sm:hover:bg-zinc-50 transition-colors min-h-[44px] text-sm sm:text-base" data-id="element-1325">
                      Cancel
                    </button>
                    <button className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg bg-[#3ab795] text-white font-medium flex items-center justify-center gap-2 sm:hover:bg-[#2da07d] transition-colors min-h-[44px]" data-id="element-1326">
                      <span className="text-sm sm:text-base" data-id="element-1327">
                        Review Offers
                      </span>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0" data-id="element-1328">
                        <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1329" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Caption */}
            <p className="text-sm text-zinc-500 mt-3 text-center" data-id="element-1330">
              Configure offer settings for multiple properties with different
              LOI templates and delivery options
            </p>
          </div>
          {/* Left: Content */}
          <div className="w-full lg:w-2/5 flex flex-col gap-8" data-id="element-1331">
            <div className="flex flex-col gap-4" data-id="element-1332">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#3ab795]/10 border border-[#3ab795]/20 backdrop-blur-sm w-fit" data-id="element-1333">
                <div className="w-2 h-2 rounded-full bg-[#3ab795] animate-pulse" data-id="element-1334" />
                <p className="text-sm md:text-base font-medium text-[#3ab795] tracking-wide uppercase" data-id="element-1335">
                  LOI Generation
                </p>
              </div>
              <h2 id="loi-generation-heading" className="text-3xl md:text-4xl font-bold text-white leading-tight" data-id="element-1336">
                Generate{' '}
                <span className="text-[#3ab795]" data-id="element-1337">
                  LOIs — Cash, Creative, or Hybrid
                </span>{' '}
                — in Seconds
              </h2>
              <p className="text-lg text-[#b8bcc8] leading-relaxed" data-id="element-1338">
                AcquireFlow automates the entire LOI creation process — from
                calculating offer prices to customizing templates for each deal
                strategy. Just select your properties, choose your LOI type, and
                send offers at scale.
              </p>
            </div>
            <div className="flex flex-col gap-5 mt-2" data-id="element-1339">
              {[{
              title: '4 LOI Types, 1 System',
              description: 'Create and send professional Letters of Intent for any strategy — Cash Offer, Subject-To Acquisition, Seller Financing, or Hybrid (Sub-To + Seller Finance) — all from one dashboard.',
              delay: 0
            }, {
              title: 'Smart Pricing Engine',
              description: 'Set offer percentages (e.g., 60% of list or ARV) and let AcquireFlow calculate property-specific offers instantly.',
              delay: 100
            }, {
              title: 'Bulk Send Automation',
              description: 'Deliver hundreds of LOIs at once via email or text message, fully personalized with agent and property details.',
              delay: 200
            }, {
              title: 'Custom Templates, Your Terms',
              description: 'Set your own contingencies, inspection periods, and financing language once — AcquireFlow auto-fills every offer you send.',
              delay: 300
            }].map((feature, index) => <div key={index} className="flex items-start gap-3 p-3 rounded-xl hover:bg-white/5 transition-all duration-300 opacity-0 translate-y-4" style={{
              animation: isVisible ? `fadeInUp 0.6s ease-out ${feature.delay}ms forwards` : 'none'
            }} data-id="element-1340">
                  <div className="w-7 h-7 rounded-full bg-[#3ab795] flex items-center justify-center flex-shrink-0 mt-1" data-id="element-1341">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-white" data-id="element-1342">
                      <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-1343" />
                    </svg>
                  </div>
                  <div data-id="element-1344">
                    <h3 className="text-lg font-semibold text-white" data-id="element-1345">
                      {feature.title}
                    </h3>
                    <p className="text-[#b8bcc8]" data-id="element-1346">{feature.description}</p>
                  </div>
                </div>)}
            </div>
            <div className="mt-6" data-id="element-1347">
              <a href="#pricing" className="inline-flex items-center gap-3 bg-white hover:bg-gray-100 text-[#1D1D1D] rounded-xl pl-7 pr-5 py-3.5 transition-all duration-300 font-medium text-lg hover:shadow-lg hover:scale-[1.02]" aria-label="Start creating LOIs" data-id="element-1348">
                Try All LOI Templates
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1349">
                  <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1350" />
                </svg>
              </a>
              <p className="text-sm text-[#b8bcc8] mt-3" data-id="element-1351">
                Any strategy, any market — all automated
              </p>
            </div>
          </div>
        </div>
        {/* Third Section: Unified Inbox for Agent Communication */}
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20" data-id="element-1352">
          {/* Left: Image Card - Agent Communication Interface - MOBILE OPTIMIZED */}
          <div className="w-full lg:w-3/5" data-id="element-1353">
            <div className="relative w-full rounded-xl sm:rounded-2xl border border-zinc-200 sm:border-2 shadow-lg sm:shadow-xl/15 overflow-hidden transition-all duration-300 sm:duration-500" data-id="element-1354">
              {/* Mobile uses min-height, tablet+ uses aspect ratio */}
              <div className="w-full relative bg-white min-h-[500px] sm:min-h-0 sm:aspect-[16/12]" data-id="element-1355">
                {/* Agent Communication UI */}
                <div className="absolute inset-0 flex flex-col" data-id="element-1356">
                  {/* Header - Mobile Optimized */}
                  <div className="bg-white px-3 sm:px-4 md:px-6 py-3 sm:py-4 border-b border-zinc-200 flex items-center justify-between" data-id="element-1357">
                    <div data-id="element-1358">
                      <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-bold text-[#1D1D1D]" data-id="element-1359">
                        Agent Communications
                      </h3>
                      <p className="text-xs sm:text-sm text-zinc-500" data-id="element-1360">
                        Manage all your offer conversations in one place
                      </p>
                    </div>
                    {/* Hide utility icons on mobile */}
                    <div className="hidden sm:flex gap-2" data-id="element-1361">
                      <div className="w-8 h-8 rounded-full bg-[#3ab795]/10 flex items-center justify-center text-[#3ab795]" data-id="element-1362">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1363">
                          <path d="M15 17H20L18.5951 15.5951C18.2141 15.2141 18 14.6973 18 14.1585V11C18 8.38757 16.3304 6.16509 14 5.34142V5C14 3.89543 13.1046 3 12 3C10.8954 3 10 3.89543 10 5V5.34142C7.66962 6.16509 6 8.38757 6 11V14.1585C6 14.6973 5.78595 15.2141 5.40493 15.5951L4 17H9M15 17V18C15 19.6569 13.6569 21 12 21C10.3431 21 9 19.6569 9 18V17M15 17H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1364" />
                        </svg>
                      </div>
                      <div className="w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-600" data-id="element-1365">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1366">
                          <path d="M12 6V12L16 14M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1367" />
                        </svg>
                      </div>
                      <div className="w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-600" data-id="element-1368">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1369">
                          <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1370" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Main Content - Stack on mobile, side-by-side on desktop */}
                  <div className="flex flex-col lg:flex-row flex-1 overflow-hidden" data-id="element-1371">
                    {/* Left Panel - Conversation List - Mobile Optimized */}
                    <div className="w-full lg:w-1/3 border-b lg:border-b-0 lg:border-r border-zinc-200 flex flex-col max-h-[250px] lg:max-h-none" data-id="element-1372">
                      {/* Search - Mobile Optimized */}
                      <div className="p-2 sm:p-3 border-b border-zinc-200" data-id="element-1373">
                        <div className="relative" data-id="element-1374">
                          <input type="text" placeholder="Search..." className="w-full bg-zinc-100 rounded-lg py-2 pl-9 pr-4 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-[#3ab795]/30" data-id="element-1375" />
                          <svg className="absolute left-3 top-2.5 w-4 h-4 text-zinc-500" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1376">
                            <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1377" />
                          </svg>
                        </div>
                      </div>

                      {/* Filter Tabs - Mobile Optimized */}
                      <div className="flex border-b border-zinc-200 overflow-x-auto" data-id="element-1378">
                        <button className="flex-1 min-w-[80px] py-2 text-xs font-medium text-[#3ab795] border-b-2 border-[#3ab795] whitespace-nowrap" data-id="element-1379">
                          All (38)
                        </button>
                        <button className="flex-1 min-w-[80px] py-2 text-xs font-medium text-zinc-500 whitespace-nowrap" data-id="element-1380">
                          Unread (12)
                        </button>
                        <button className="flex-1 min-w-[80px] py-2 text-xs font-medium text-zinc-500 whitespace-nowrap" data-id="element-1381">
                          Flagged (5)
                        </button>
                      </div>

                      {/* Conversation List - Scrollable */}
                      <div className="flex-1 overflow-y-auto" data-id="element-1382">
                        {/* Active Conversation */}
                        <div className="p-2 sm:p-2.5 bg-[#f8fdfb] border-l-4 border-[#3ab795]" data-id="element-1383">
                          <div className="flex justify-between mb-1" data-id="element-1384">
                            <h4 className="font-medium text-xs sm:text-sm text-[#1D1D1D]" data-id="element-1385">
                              Sarah Johnson
                            </h4>
                            <span className="text-xs text-zinc-500" data-id="element-1386">
                              2m ago
                            </span>
                          </div>
                          <div className="flex justify-between items-start" data-id="element-1387">
                            <div className="min-w-0 flex-1" data-id="element-1388">
                              <p className="text-xs text-zinc-600 line-clamp-1" data-id="element-1389">
                                Yes, I received your offer for 3737...
                              </p>
                              <p className="text-xs text-[#3ab795] mt-0.5 truncate" data-id="element-1390">
                                3737 Casa Del Sol Ln
                              </p>
                            </div>
                            <div className="flex flex-col items-end ml-2" data-id="element-1391">
                              <span className="bg-[#3ab795] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0" data-id="element-1392">
                                2
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Regular Conversation */}
                        <div className="p-2 sm:p-2.5 border-b border-zinc-100" data-id="element-1393">
                          <div className="flex justify-between mb-1" data-id="element-1394">
                            <h4 className="font-medium text-xs sm:text-sm text-[#1D1D1D]" data-id="element-1395">
                              Michael Rodriguez
                            </h4>
                            <span className="text-xs text-zinc-500 flex-shrink-0 ml-2" data-id="element-1396">
                              Yesterday
                            </span>
                          </div>
                          <div className="flex justify-between items-start" data-id="element-1397">
                            <div className="min-w-0" data-id="element-1398">
                              <p className="text-xs text-zinc-600 line-clamp-1" data-id="element-1399">
                                I'm interested in discussing...
                              </p>
                              <p className="text-xs text-zinc-500 mt-0.5 truncate" data-id="element-1400">
                                8324 Coolgreene Dr
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Scheduled Follow-up */}
                        <div className="p-2 sm:p-2.5 border-b border-zinc-100" data-id="element-1401">
                          <div className="flex justify-between mb-1" data-id="element-1402">
                            <h4 className="font-medium text-xs sm:text-sm text-[#1D1D1D]" data-id="element-1403">
                              David Wilson
                            </h4>
                            <span className="text-xs text-zinc-500 flex-shrink-0 ml-2" data-id="element-1404">
                              2d ago
                            </span>
                          </div>
                          <div className="flex justify-between items-start" data-id="element-1405">
                            <div className="min-w-0" data-id="element-1406">
                              <p className="text-xs text-zinc-600 line-clamp-1" data-id="element-1407">
                                Follow-up scheduled...
                              </p>
                              <p className="text-xs text-zinc-500 mt-0.5 truncate" data-id="element-1408">
                                10866 Cassandra Way
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Right Panel - Active Conversation - Mobile Optimized */}
                    <div className="w-full lg:w-2/3 flex flex-col flex-1" data-id="element-1409">
                      {/* Conversation Header - Mobile Optimized */}
                      <div className="p-2 sm:p-3 border-b border-zinc-200 flex justify-between items-center" data-id="element-1410">
                        <div className="flex items-center gap-2 min-w-0 flex-1" data-id="element-1411">
                          <div className="w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-600 font-medium text-xs sm:text-sm flex-shrink-0" data-id="element-1412">
                            SJ
                          </div>
                          <div className="min-w-0 flex-1" data-id="element-1413">
                            <h4 className="font-medium text-xs sm:text-sm text-[#1D1D1D] truncate" data-id="element-1414">
                              Sarah Johnson
                            </h4>
                            <p className="text-xs text-zinc-500 truncate" data-id="element-1415">
                              Agent • ABC Realty • Online now
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-1 sm:gap-2 flex-shrink-0" data-id="element-1416">
                          <button className="p-1.5 rounded-lg sm:hover:bg-zinc-100 transition-colors min-h-[36px] min-w-[36px] flex items-center justify-center" data-id="element-1417">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1418">
                              <path d="M3 5H11M3 12H16M3 19H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1419" />
                            </svg>
                          </button>
                          <button className="p-1.5 rounded-lg sm:hover:bg-zinc-100 transition-colors min-h-[36px] min-w-[36px] flex items-center justify-center" data-id="element-1420">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1421">
                              <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1422" />
                            </svg>
                          </button>
                        </div>
                      </div>

                      {/* Property Context - Mobile Optimized */}
                      <div className="p-2 sm:p-2.5 bg-[#f8fdfb] flex items-center justify-between gap-2" data-id="element-1423">
                        <div className="flex items-center gap-2 min-w-0 flex-1" data-id="element-1424">
                          <div className="w-6 h-6 sm:w-7 sm:h-7 bg-[#3ab795]/10 rounded-full flex items-center justify-center text-[#3ab795] flex-shrink-0" data-id="element-1425">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1426">
                              <path d="M3 12L5 10M5 10L12 3L19 10M5 10V20C5 20.5523 5.44772 21 6 21H9M19 10L21 12M19 10V20C19 20.5523 18.5523 21 18 21H15M9 21C9.55228 21 10 20.5523 10 20V16C10 15.4477 10.4477 15 11 15H13C13.5523 15 14 15.4477 14 16V20C14 20.5523 14.4477 21 15 21M9 21H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1427" />
                            </svg>
                          </div>
                          <div className="min-w-0 flex-1" data-id="element-1428">
                            <p className="text-xs sm:text-sm font-medium text-[#1D1D1D] truncate" data-id="element-1429">
                              3737 Casa Del Sol Ln, Dallas, TX
                            </p>
                            <p className="text-xs text-zinc-500 truncate" data-id="element-1430">
                              Cash Offer: $240,730 (70% of list)
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-1 flex-shrink-0" data-id="element-1431">
                          <button className="text-xs font-medium text-[#3ab795] py-1.5 px-2 sm:px-3 rounded sm:hover:bg-[#3ab795]/10 transition-colors min-h-[36px]" data-id="element-1432">
                            View
                          </button>
                        </div>
                      </div>

                      {/* Messages - Scrollable */}
                      <div className="flex-1 p-2 sm:p-3 bg-[#fafafa] space-y-2 overflow-y-auto" data-id="element-1433">
                        {/* System Message */}
                        <div className="text-center" data-id="element-1434">
                          <span className="text-xs bg-zinc-200 text-zinc-600 py-0.5 px-2 rounded-full" data-id="element-1435">
                            May 15, 2023
                          </span>
                        </div>

                        {/* Outgoing Message */}
                        <div className="flex justify-end" data-id="element-1436">
                          <div className="max-w-[85%] bg-[#3ab795] text-white p-2 sm:p-2.5 rounded-lg rounded-tr-none" data-id="element-1437">
                            <p className="text-xs sm:text-sm" data-id="element-1438">
                              Hello Sarah, I've sent you a cash offer for 3737
                              Casa Del Sol Ln at $240,730. Clean offer with
                              14-day inspection, can close in 10 days.
                            </p>
                            <p className="text-xs text-right mt-1 text-white/70" data-id="element-1439">
                              10:32 AM
                            </p>
                          </div>
                        </div>

                        {/* Incoming Message */}
                        <div className="flex" data-id="element-1440">
                          <div className="max-w-[85%] bg-white p-2 sm:p-2.5 rounded-lg rounded-tl-none shadow-sm" data-id="element-1441">
                            <p className="text-xs sm:text-sm" data-id="element-1442">
                              Thank you for your offer. I've shared it with my
                              client. They have questions about inspection and
                              closing timeline.
                            </p>
                            <p className="text-xs text-right mt-1 text-zinc-500" data-id="element-1443">
                              11:45 AM
                            </p>
                          </div>
                        </div>

                        {/* Outgoing Message */}
                        <div className="flex justify-end" data-id="element-1444">
                          <div className="max-w-[85%] bg-[#3ab795] text-white p-2 sm:p-2.5 rounded-lg rounded-tr-none" data-id="element-1445">
                            <p className="text-xs sm:text-sm" data-id="element-1446">
                              We're flexible! Can reduce inspection to 7 days
                              and close in as little as 7 days if needed.
                            </p>
                            <p className="text-xs text-right mt-1 text-white/70" data-id="element-1447">
                              12:03 PM
                            </p>
                          </div>
                        </div>

                        {/* Incoming Message */}
                        <div className="flex" data-id="element-1448">
                          <div className="max-w-[85%] bg-white p-2 sm:p-2.5 rounded-lg rounded-tl-none shadow-sm" data-id="element-1449">
                            <p className="text-xs sm:text-sm" data-id="element-1450">
                              That's helpful. Would you consider increasing to
                              closer to $250,000?
                            </p>
                            <p className="text-xs text-right mt-1 text-zinc-500" data-id="element-1451">
                              1:15 PM
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Message Input - Mobile Optimized Touch Target */}
                      <div className="p-2 sm:p-2.5 border-t border-zinc-200 bg-white" data-id="element-1452">
                        <div className="flex items-end gap-2" data-id="element-1453">
                          <div className="flex-1 bg-zinc-100 rounded-lg p-2 min-h-[44px] flex items-center" data-id="element-1454">
                            <div className="text-xs sm:text-sm text-zinc-400" data-id="element-1455">
                              Type your message...
                            </div>
                          </div>
                          <button className="w-11 h-11 bg-[#3ab795] text-white rounded-lg flex items-center justify-center flex-shrink-0 sm:hover:bg-[#2da07d] transition-colors" data-id="element-1456">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1457">
                              <path d="M22 2L11 13M22 2L15 22L11 13M11 13L2 9L22 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1458" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Caption */}
            <p className="text-sm text-zinc-500 mt-3 text-center" data-id="element-1459">
              Unified inbox for managing all agent communications, with property
              context and team collaboration
            </p>
          </div>

          {/* Right: Content */}
          <div className="w-full lg:w-2/5 flex flex-col gap-8" data-id="element-1460">
            <div className="flex flex-col gap-4" data-id="element-1461">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#3ab795]/10 border border-[#3ab795]/20 backdrop-blur-sm w-fit" data-id="element-1462">
                <div className="w-2 h-2 rounded-full bg-[#3ab795] animate-pulse" data-id="element-1463" />
                <p className="text-sm md:text-base font-medium text-[#3ab795] tracking-wide uppercase" data-id="element-1464">
                  Agent Communication
                </p>
              </div>
              <h2 id="unified-inbox-heading" className="text-3xl md:text-4xl font-bold text-white leading-tight" data-id="element-1465">
                <span className="text-[#3ab795]" data-id="element-1466">Unified Inbox</span> for Agent
                Communication
              </h2>
              <p className="text-lg text-[#b8bcc8] leading-relaxed" data-id="element-1467">
                Manage every offer, response, and negotiation from one
                centralized inbox. No more chasing emails or digging through
                texts — every conversation stays linked to the property, agent,
                and offer it belongs to.
              </p>
            </div>
            <div className="flex flex-col gap-5 mt-2" data-id="element-1468">
              {[{
              title: 'Centralized Messaging',
              description: 'All agent replies and offer updates sync directly into one thread — organized by property and offer status.',
              delay: 0
            }, {
              title: 'Smart Follow-Up Sequences',
              description: "Set rules to automatically follow up with agents who haven't replied — keeping your deals moving without lifting a finger.",
              delay: 100
            }, {
              title: 'Team Collaboration',
              description: 'Tag team members, assign follow-ups, and track communication history across your entire acquisitions pipeline.',
              delay: 200
            }].map((feature, index) => <div key={index} className="flex items-start gap-3 p-3 rounded-xl hover:bg-white/5 transition-all duration-300 opacity-0 translate-y-4" style={{
              animation: isVisible ? `fadeInUp 0.6s ease-out ${feature.delay}ms forwards` : 'none'
            }} data-id="element-1469">
                  <div className="w-7 h-7 rounded-full bg-[#3ab795] flex items-center justify-center flex-shrink-0 mt-1" data-id="element-1470">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-white" data-id="element-1471">
                      <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-1472" />
                    </svg>
                  </div>
                  <div data-id="element-1473">
                    <h3 className="text-lg font-semibold text-white" data-id="element-1474">
                      {feature.title}
                    </h3>
                    <p className="text-[#b8bcc8]" data-id="element-1475">{feature.description}</p>
                  </div>
                </div>)}
            </div>
            <div className="mt-6" data-id="element-1476">
              <p className="text-xl font-medium text-white mb-4" data-id="element-1477">
                Stop juggling inboxes — start closing faster.
              </p>
              <a href="#pricing" className="inline-flex items-center gap-3 bg-white hover:bg-gray-100 text-[#1D1D1D] rounded-xl pl-7 pr-5 py-3.5 transition-all duration-300 font-medium text-lg hover:shadow-lg hover:scale-[1.02]" aria-label="Start your free trial" data-id="element-1478">
                Start Your Free Trial
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1479">
                  <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1480" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>;
};